import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import Card from '../../components/base/Card';
import { Link } from 'react-router-dom';

export default function AboutPage() {
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});

  const handleImageError = (imageKey: string) => {
    setImageErrors(prev => ({ ...prev, [imageKey]: true }));
  };

  const teamMembers = [
    {
      name: "Alex Rodriguez",
      role: "CEO & AI Consultant",
      image: "https://public.readdy.ai/ai/img_res/61ff926005f4aec50c9757648446b334.jpg",
      bio: "15+ years in AI and digital transformation with expertise in automation solutions."
    },
    {
      name: "Sarah Chen",
      role: "Head of Product Development",
      image: "https://readdy.ai/api/search-image?query=Professional%20female%20tech%20executive%20portrait%2C%20product%20manager%20in%20modern%20workspace%2C%20business%20attire%2C%20confident%20professional%20headshot%2C%20clean%20background&width=300&height=300&seq=team2&orientation=squarish",
      bio: "Former Silicon Valley product manager specializing in digital product creation and optimization."
    },
    {
      name: "Michael Thompson",
      role: "Lead Developer",
      image: "https://readdy.ai/api/search-image?query=Professional%20software%20developer%20portrait%2C%20tech%20lead%20in%20modern%20office%2C%20casual%20business%20attire%2C%20confident%20programmer%20headshot%2C%20clean%20background&width=300&height=300&seq=team3&orientation=squarish",
      bio: "Full-stack developer with expertise in AI integration and automation systems."
    }
  ];

  const stats = [
    { number: "500+", label: "Digital Products Created" },
    { number: "10,000+", label: "Satisfied Customers" },
    { number: "98%", label: "Customer Satisfaction" },
    { number: "24/7", label: "Support Available" }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-gradient-to-r from-blue-900 to-purple-900 text-white py-20"
        style={{
          backgroundImage: `linear-gradient(rgba(30, 58, 138, 0.8), rgba(88, 28, 135, 0.8)), url('https://readdy.ai/api/search-image?query=Modern%20business%20team%20collaboration%20in%20high-tech%20office%2C%20professional%20workspace%20with%20digital%20screens%2C%20team%20meeting%2C%20innovation%20and%20technology%2C%20clean%20corporate%20environment&width=1200&height=600&seq=about-hero&orientation=landscape')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6">About <span style={{ fontFamily: 'Eurostile, sans-serif' }}>Digital Product Authority</span></h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            We are pioneers in digital product creation and AI consulting, empowering businesses 
            with cutting-edge automation solutions and premium digital products.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Mission</h2>
              <p className="text-lg text-gray-700 mb-6">
                At Digital Product Authority, we believe in the transformative power of technology. 
                Our mission is to democratize access to premium digital solutions that were once 
                available only to large corporations.
              </p>
              <p className="text-lg text-gray-700 mb-6">
                We specialize in creating automated workflows, AI-powered tools, and comprehensive 
                digital products that streamline operations, increase efficiency, and drive growth 
                for businesses of all sizes.
              </p>
              <div className="flex space-x-4">
                <Link to="/contact">
                  <Button>
                    <i className="ri-phone-line"></i>
                    Contact Us
                  </Button>
                </Link>
                <Link to="/products">
                  <Button variant="outline">
                    <i className="ri-download-line"></i>
                    Our Portfolio
                  </Button>
                </Link>
              </div>
            </div>
            <div>
              <Card className="overflow-hidden">
                <div className="w-full h-80 bg-gray-100">
                  <img 
                    src={imageErrors['mission'] 
                      ? "https://readdy.ai/api/search-image?query=Modern%20AI%20technology%20workspace%20with%20automation%20tools%2C%20digital%20transformation%20concept%2C%20futuristic%20office%20environment%2C%20clean%20professional%20setting%2C%20technology%20innovation%2C%20bright%20lighting&width=600&height=400&seq=mission-fallback&orientation=landscape"
                      : "https://readdy.ai/api/search-image?query=Modern%20AI%20technology%20workspace%20with%20automation%20tools%2C%20digital%20transformation%20concept%2C%20futuristic%20office%20environment%2C%20clean%20professional%20setting%2C%20technology%20innovation&width=600&height=400&seq=mission&orientation=landscape"
                    }
                    alt="Our Mission"
                    className="w-full h-full object-cover object-top"
                    onError={() => handleImageError('mission')}
                    loading="lazy"
                  />
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Impact</h2>
            <p className="text-xl text-gray-600">
              Numbers that reflect our commitment to excellence
            </p>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl lg:text-5xl font-bold text-blue-600 mb-2">{stat.number}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Meet Our Team</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our expert team combines decades of experience in AI, automation, and digital product development
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {teamMembers.map((member, index) => (
              <Card key={index} className="text-center p-8">
                <div className="w-32 h-32 mx-auto mb-6 rounded-full overflow-hidden bg-gray-100">
                  <img 
                    src={imageErrors[`team${index}`]
                      ? `https://readdy.ai/api/search-image?query=Professional%20business%20person%20portrait%20headshot%2C%20confident%20executive%20in%20modern%20office%2C%20business%20attire%2C%20clean%20background%2C%20corporate%20photography%20style&width=300&height=300&seq=team-fallback${index}&orientation=squarish`
                      : member.image
                    }
                    alt={member.name}
                    className="w-full h-full object-cover object-top"
                    onError={() => handleImageError(`team${index}`)}
                    loading="lazy"
                  />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{member.name}</h3>
                <p className="text-blue-600 font-medium mb-4">{member.role}</p>
                <p className="text-gray-600">{member.bio}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Values</h2>
            <p className="text-xl text-gray-600">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: "ri-lightbulb-line",
                title: "Innovation",
                description: "Constantly pushing boundaries with cutting-edge solutions"
              },
              {
                icon: "ri-shield-check-line",
                title: "Quality",
                description: "Delivering premium products that exceed expectations"
              },
              {
                icon: "ri-customer-service-line",
                title: "Support",
                description: "Providing exceptional customer service and guidance"
              },
              {
                icon: "ri-rocket-line",
                title: "Growth",
                description: "Empowering businesses to scale and succeed"
              }
            ].map((value, index) => (
              <Card key={index} className="text-center p-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${value.icon} text-2xl text-blue-600`}></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Work Together?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Let's discuss how our digital products and AI consulting services can transform your business operations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
                <i className="ri-phone-line"></i>
                Schedule Consultation
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600">
                <i className="ri-mail-line"></i>
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
